<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type {
  IAffiliateProgramCommissionFilterStepItemAttributePayload,
  IAffiliateProgramCommissionFilterStepPayload,
  ICommissionStepItemSchema,
} from '@modules/affiliate-program-commissions/submodules/edit/types';
import type { VFormStepsWidgetStep } from '@modules/form-widgets/components/VFormStepsWidget/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS } from '@modules/affiliate-program-commissions/constants';
import { isInvalidStep } from '@modules/affiliate-program-commissions/submodules/edit/components/AffiliateProgramCommissionsForm/composables/utils';
import { filterOutAttributeSchema } from '@modules/affiliate-program-commissions/submodules/edit/utils';
import VFormStepsWidget from '@modules/form-widgets/components/VFormStepsWidget/VFormStepsWidget.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { watchImmediate } from '@vueuse/core';

import type {
  CommissionStepsEmits,
  CommissionStepsProps,
} from './types';

const props = withDefaults(defineProps<CommissionStepsProps>(), {});
const emit = defineEmits<CommissionStepsEmits>();

const { getTranslation } = useI18n();

const tempSteps = ref<IAffiliateProgramCommissionFilterStepPayload[]>([]);

watchImmediate(() => props.steps, (newValue) => {
  tempSteps.value = newValue.map((item) => item.value);
}, { deep: true });

const hasAnyCpaStep = computed(() => (
  props.steps.some((step) => (
    step.value.condition_step_items_attributes.some((condition) => (
      condition.condition_for_comparison === 'any'
    ))
  ))
));

const stepFormSchema = computed<FormSchema<ICommissionStepItemSchema>>(() => {
  const formSchema: FormSchema<ICommissionStepItemSchema> = {
    payment_size: {
      type: EFormInputTypes.NUMBER,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_CONDITION_FORM_VALUE').value,
      required: true,
    },
  };

  if (props.productPaymentColumnOptions) {
    formSchema.payment_column = {
      type: EFormInputTypes.SELECT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_CONDITION_FORM_PAYMENT_COLUMN').value,
      options: props.productPaymentColumnOptions,
      required: true,
    };
  }

  return formSchema;
});

const comparisonOptions = computed<IFormSelectInputOption<string>[]>(() => (
  props.steps.length > 1
    ? props.comparisonOptions.filter((item) => item.value !== 'any')
    : props.comparisonOptions
));

const stepResultFormSchema = computed<FormSchema<IAffiliateProgramCommissionFilterStepItemAttributePayload>>(() => ({
  obtained_result: {
    type: EFormInputTypes.SELECT,
    options: props.obtainedResultOptions,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_FORM_OBTAINED_RESULT').value,
    required: true,
  },
  condition_for_comparison: {
    type: EFormInputTypes.SELECT,
    options: comparisonOptions.value,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_MAIN_TITLE_EDIT_CONDITION_FOR_COMPARISON').value,
    required: true,
  },
  desired_value: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_MAIN_TITLE_EDIT_DESIRED_VALUE').value,
    required: true,
  },
}));

const formSteps = computed<VFormStepsWidgetStep<ICommissionStepItemSchema, IAffiliateProgramCommissionFilterStepItemAttributePayload>[]>(() => (
  props.steps.map((item, index) => ({
    step: {
      formSchema: stepFormSchema.value,
      formTitle: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_CONDITION_TITLE').value,
      formSubmitBtn: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_CONDITION_SAVE').value,
      formCloseBtn: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_CONDITION_CANCEL').value,
      label: getTranslation(
        TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS,
        'STEPS_PAYMENT_SIZE_EMPTY',
        {
          settings: {
            constant: {
              mark: props.stepValueSuffix,
            },
          },
        },
      ).value,
      value: {
        payment_size: item.value.payment_size,
        payment_column: item.value.payment_column,
      },
      valueSuffix: props.stepValueSuffix,
    },
    value: {
      formSchema: filterOutAttributeSchema(stepResultFormSchema.value, 0, tempSteps.value[index]?.condition_step_items_attributes),
      formTitle: stepResultFormSchema.value
        ? getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_MAIN_TITLE_EDIT').value
        : getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_MAIN_TITLE_ADD').value,
      formSubmitBtn: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_SAVE').value,
      formCloseBtn: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_CANCEL').value,
      value: item.value.condition_step_items_attributes[0],
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'STEPS_ENTER_CONDITION').value,
    },
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'STEPS_STEP', { index: index + 1 }).value.toUpperCase(),
    allowRemove: true,
    errors: isInvalidStep(item.value, props.transactionType)
      ? [
          getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'VALIDATIONS_STEP_INVALID').value,
        ]
      : undefined,
  } as VFormStepsWidgetStep<ICommissionStepItemSchema, IAffiliateProgramCommissionFilterStepItemAttributePayload>))
));

function onAdd(): void {
  emit('add', {
    transactionType: props.transactionType,
    product: props.product,
  });
}

function onRemove(index: number): void {
  const button = props.steps[index];

  if (button) {
    emit('remove', button.index);
  }
}

function onChange(index: number, step?: ICommissionStepItemSchema, value?: IAffiliateProgramCommissionFilterStepItemAttributePayload): void {
  const button = props.steps[index];

  if (button) {
    emit('change', button.index, {
      ...button.value,
      ...(step ?? {}),
      condition_step_items_attributes: value
        ? [
            {
              ...(button.value.condition_step_items_attributes[0] || {}),
              ...value,
              desired_value: value.condition_for_comparison === 'any' ? undefined : value.desired_value,
            },
          ]
        : button.value.condition_step_items_attributes,
    });
  }
}

function onInput(index: number, step?: ICommissionStepItemSchema, value?: IAffiliateProgramCommissionFilterStepItemAttributePayload): void {
  const button = tempSteps.value[index];

  if (button) {
    tempSteps.value.splice(index, 1, {
      ...button,
      ...(step ?? {}),
      condition_step_items_attributes: value
        ? [
            {
              ...(button.condition_step_items_attributes || {}),
              ...value,
            },
          ]
        : button.condition_step_items_attributes,
    });
  }
}
</script>

<template>
  <VFormStepsWidget v-data-qa-auto-id="'CommissionSteps'"
    :steps="formSteps"
    :add-label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'STEPS_ADD_STEP')"
    :info-text="hasAnyCpaStep && !readonly ? $getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'STEPS_TOOLTIP_ANY') : undefined"
    :errors="errors"
    :readonly="readonly"
    @add="onAdd"
    @remove="onRemove"
    @change="onChange"
    @input="onInput"
  />
</template>
